import { useState } from 'react';
import { Button } from '@/shared/components/Button';
import { Modal } from '@/shared/components/Modal';
import { toast } from '@/shared/components/Toast';
import { useAuthStore } from '@/features/auth/stores/auth-store';
import { useAgentRelease, usePrintingStatus, useRequestAgentUpdate } from '../hooks/usePrinting';
import { usePrintingAvailable } from '../hooks/usePrintingAvailable';
import {
  classifyUpdateRequestError,
  describeUpdateRequested,
  isRefusedRead,
  lastUpdateAttemptFailed,
  pickUpdatePrompt,
  promptKey,
  UPDATE_REQUEST_FAILED,
  useAgentUpdatePromptStore,
  type UpdatePrompt,
} from '../lib/agent-update';
import { formatTime } from '../lib/printing-format';
import type { AgentUpdateMode } from '../types/printing.types';
import styles from './AgentUpdatePrompt.module.css';

/**
 * Aviso de atualização recomendada e diálogo de atualização obrigatória do computador
 * da impressão, em qualquer tela do painel (change print-agent).
 *
 * Só para quem pode pedir (`settings.write`) e com a impressão disponível: a loja no
 * piloto, e o estado sem desligamento geral nem assinatura pendente. Atendente e
 * cozinha não veem, porque não têm o que fazer. O painel inteiro, e não só a aba
 * Impressão, porque o dono raramente volta à aba depois de ativar.
 */
export function AgentUpdatePrompt() {
  const { available } = usePrintingAvailable('settings.write');
  if (!available) return null;
  return <AgentUpdatePromptContent />;
}

function AgentUpdatePromptContent() {
  const tenantId = useAuthStore((state) => state.selectedTenant?.id ?? '');
  const readsStatus = useAuthStore((state) => state.user?.permissions.includes('orders.read') ?? false);
  const closed = useAgentUpdatePromptStore((state) => state.closed);

  // O desligamento geral e a assinatura pendente vêm do estado, que é de quem vê pedidos.
  // Com eles, o pedido seria recusado: nem aviso nem diálogo.
  const status = usePrintingStatus(readsStatus);
  const printingOpen =
    readsStatus &&
    !!status.data &&
    !status.data.platformDisabled &&
    !status.data.accessBlocked &&
    !isRefusedRead(status.error);

  const release = useAgentRelease(printingOpen);
  if (!printingOpen || !release.data || isRefusedRead(release.error)) return null;

  const prompt = pickUpdatePrompt(
    release.data,
    (agentId, level) => closed[promptKey(tenantId, agentId, level)] === true,
  );
  if (!prompt) return null;

  // A chave recomeça o diálogo (e a mensagem de erro) quando muda o computador ou o nível.
  return <UpdateDialog key={`${prompt.agent.agentId}:${prompt.level}`} prompt={prompt} tenantId={tenantId} />;
}

function UpdateDialog({ prompt, tenantId }: { prompt: UpdatePrompt; tenantId: string }) {
  const { agent, level } = prompt;
  const mandatory = level === 'mandatory';
  const request = useRequestAgentUpdate();
  const closeForTab = useAgentUpdatePromptStore((state) => state.close);
  const [error, setError] = useState<string | null>(null);
  const [choosing, setChoosing] = useState<AgentUpdateMode | null>(null);

  // Fecha no nível mostrado: o "Depois" do recomendado não esconde o obrigatório que vier.
  const closeHere = () => closeForTab(promptKey(tenantId, agent.agentId, level));

  // `mutateAsync`, e não o retorno do `mutate`: a resposta entra no cache e fecha o
  // diálogo antes de o retorno rodar, e o React Query não chama o retorno de um
  // componente desmontado. A promessa resolve do mesmo jeito.
  const choose = async (mode: AgentUpdateMode) => {
    setError(null);
    setChoosing(mode);
    try {
      const result = await request.mutateAsync({ agentId: agent.agentId, mode, level });
      toast('success', describeUpdateRequested(result));
    } catch (failure) {
      const kind = classifyUpdateRequestError(failure);
      // Em andamento ou desnecessária: a API diz que não há mais o que pedir.
      if (kind === 'settled') closeHere();
      // Rede, 5xx ou corrida perdida: continua aberto, com a mensagem.
      if (kind === 'retry') setError(UPDATE_REQUEST_FAILED);
      // As recusas da impressão (assinatura, piloto, desligamento geral) só releem o
      // estado, o que a gravação já faz: a releitura esconde o aviso e o diálogo.
    } finally {
      setChoosing(null);
    }
  };

  const busy = request.isPending;
  const endOfDay = formatTime(agent.endOfDayAt);

  const footer = (
    <div className={styles.footer}>
      {mandatory ? (
        <Button
          variant="secondary"
          onClick={() => void choose('end_of_day')}
          loading={choosing === 'end_of_day'}
          disabled={busy}
        >
          {`No fim do expediente de hoje (às ${endOfDay})`}
        </Button>
      ) : (
        <Button variant="secondary" onClick={closeHere} disabled={busy}>
          Depois
        </Button>
      )}
      <Button onClick={() => void choose('now')} loading={choosing === 'now'} disabled={busy}>
        Atualizar agora
      </Button>
    </div>
  );

  return (
    <Modal
      open
      // O obrigatório não fecha por Esc, clique fora nem botão: só pela escolha.
      dismissible={!mandatory}
      onClose={mandatory ? () => undefined : closeHere}
      title={
        mandatory
          ? 'Atualização obrigatória do computador da impressão'
          : 'Atualização do computador da impressão'
      }
      size="sm"
      footer={footer}
    >
      <div className={styles.body} data-testid={mandatory ? 'agent-update-mandatory' : 'agent-update-recommended'}>
        <p className={styles.lead}>
          {mandatory
            ? `O computador "${agent.name}" precisa atualizar o programa da impressão.`
            : `Há uma versão nova do programa da impressão para o computador "${agent.name}".`}
        </p>
        {agent.agentVersion && <p className={styles.text}>Versão instalada: {agent.agentVersion}.</p>}

        {mandatory && !agent.protocolSupported && (
          <p className={styles.stopped}>
            A impressão está parada: os pedidos não estão saindo no papel até o computador atualizar.
          </p>
        )}
        {mandatory && agent.protocolSupported && (
          <p className={styles.text}>A impressão continua funcionando até a atualização.</p>
        )}
        {lastUpdateAttemptFailed(agent) && (
          <p className={styles.lastFailed}>A última tentativa de atualização não deu certo.</p>
        )}

        <p className={styles.text}>
          A atualização começa quando não houver cupom na fila, leva poucos minutos e não precisa de ninguém
          no computador.
        </p>

        {error && (
          <p className={styles.error} role="alert">
            {error}
          </p>
        )}
      </div>
    </Modal>
  );
}
